import * as React from "react"
import {formatFloat, formatInt, removeNulls, secsToHMS} from "../../lib/utils";
import {
	AllTabData,
	CallbackProps, ContentDetail, DashboardSourcesEntry, DashboardVisitorsEntry, EcommerceDetail, HasValidKeywordsData,
	Keywords,
	KeywordsDetail, SourceInfo, Tab,
	ValueProps
} from "./app";
import {Panel as GenericPanel, PanelProps, Tabs as GenericTabs, TabsProps} from "../../lib/sbhtml/tabs";
import {PageCallbackProps, PageProps, PaginatedDataGrid, Row} from "../../lib/sbhtml/fixed-data-grid";
import {Modal} from "../../lib/sbhtml/modal";
import {DatePicker} from "../../lib/sbhtml/datePicker"
import {HelpButton} from "../../lib/help button";
import {Line, Pie} from "react-chartjs-2"

export const ApplyButton = (props: CallbackProps) => <button type="button" onClick={props.updateVisitsData}>Apply</button>

export type VisitsColors = {
	visitsColor: string
	newVisitsColor: string
	returningVisitsColor: string
	pageviewsColor: string,
}

export type SourceColors = {
	directColor: string,
	searchColor: string,
	otherColor: string
	referringColor: string
}

type OverviewModalProps = VisitsColors & {
	onRequestCloseModal: () => void
	day: DashboardVisitorsEntry
	keywords: Keywords
}

const Keywords = (p: OverviewModalProps & {keywords: HasValidKeywordsData}) =>
	<table className="adminlist tablelist tablesorter" id="topkeywords2" style={{
		width: "100%",
		border: 0
	}} cellSpacing={0} cellPadding={5}>
		<thead>
		<tr>
			<th />
			<th style={{width: "50%", textAlign: "left"}}>Keyword</th>
			<th style={{width: "20%", textAlign: "right"}}>Visits</th>
			<th style={{width: "30%", textAlign: "right"}}>Avg. Time On Site</th>
		</tr>
		</thead>
		<tbody>
		{p.keywords.result.map((k, i) =>
			<tr key={i}>
				<td style={{textAlign:"right"}}>{i + 1}.</td>
				<td style={{width:"50%"}}>{k.keyword}</td>
				<td style={{color: "#000", width:"20%", textAlign:"right"}}>{k.visitsTotal}</td>
				<td style={{color: "#000", width:"30%", align:"right"}}>{k.averageTime}</td>
			</tr>
		)}
		</tbody>
	</table>

const OverviewModal = (p: OverviewModalProps) => {
	const keywords = p.keywords
	let keywordsWidget
	switch (keywords.type) {
		case "has result":
			keywordsWidget = <Keywords {...p} keywords={keywords} />
			break
		case "loading":
			keywordsWidget = <img src="/sb/static/images/loadergif.gif" />
			break
		case "error":
			keywordsWidget = <p>There was an error loading the top keywords data. Make sure your internet connection works, then try again. Try reloading the page. If the problem persists, consider reporting SB494A to AO tech support.</p>
			break
	}

	return <Modal style={{
		width: 800,
		height: 340,
	}} isOpen={true} title={"Overview of " + p.day.PopupDate} onRequestClose={p.onRequestCloseModal}>
		<div id="single">
			<span style={{
				color: p.visitsColor,
				fontSize: "20px",
				fontWeight: "bold"
			}}>Visits: {p.day.visits}</span><br />
			<span style={{color: p.newVisitsColor}}>New Visitors: {p.day.newVisits}</span><br />
			<span style={{color: p.returningVisitsColor}}>Returning Visitors: {p.day.visits - p.day.newVisits}</span><br />
			<span style={{color: p.pageviewsColor}}>Pageviews: {p.day.pageviews}</span><br /><br />
			<h3 style={{margin: "0 0 5px", color: "#036"}}>Top 5 Keywords</h3>
			{keywordsWidget}
		</div>
	</Modal>
}

export const Dashboard = (p: ValueProps & CallbackProps) => <div>
	{p.overviewing && <OverviewModal
		visitsColor={p.visitsColor}
		returningVisitsColor={p.returningVisitsColor}
		newVisitsColor={p.newVisitsColor}
		pageviewsColor={p.pageviewsColor}
		day={p.overviewing}
		keywords={p.keywords}
		onRequestCloseModal={p.onRequestCloseOverview}
	/> }
	<table cellSpacing={0} cellPadding={0} style={{borderBottom: "1px solid #ccc", width: 1000}}>
		<tbody>
		<tr>
			<td>
				<h2 className="sbHeading">Dashboard</h2>
			</td>
			<td>
				<table style={{marginLeft: 'auto'}}>
					<tbody>
					<tr>
						<td>
							{(p.loading == "loading") && <img src="/sb/static/images/loadergif.gif" />}
							{(p.loading == "failed") && <img src="/sb/static/images/icons/cross.gif" />}
							<strong>Segments:</strong>
						</td>
						<td>
							<form style={{margin: 0}}>
								<label className="checkbox" style={{color: "#666666"}}>
									<input onChange={(e) => p.setNewVisitors(e.currentTarget.checked)} checked={p.includesNewVisitors} value="1" type="checkbox"/>
									New Visitors
								</label>
								<label className="checkbox" style={{color: "#666666"}}>
									<input onChange={(e) => p.setReturningVisitors(e.currentTarget.checked)} checked={p.includesReturningVisitors} value="2" type="checkbox"/>
									Returning Visitors
								</label>
								<ApplyButton {...p} />
							</form>
						</td>
						<td style={{paddingLeft: 20}}>
							<form style={{margin: 0}}>
								<DatePicker onChange={(e, d) => p.setFromDate(d)} date={p.fromDate} readonly={false} invalid={false}/>
								<DatePicker onChange={(e, d) => p.setToDate(d)} date={p.toDate} readonly={false} invalid={false}/>
								<ApplyButton {...p} />
							</form>
						</td>
					</tr>
					</tbody>
				</table>
			</td>
		</tr>
		</tbody>
	</table>
	<table cellSpacing={0} cellPadding={0} style={{width: "100%", border: 0}}>
		<tbody>
		<tr>
			<td>
				<div id="charts2">
					<div id="visitsDiv2" style={{width: 1000, height: 230}}>
						<Line options={{maintainAspectRatio: false, legend: {position: 'bottom'}, title: {display: true, text: "Visits for all visitors"}}} onElementsClick={(e) => e[0] && p.showDayById(e[0]._index)} data={{labels: p.labels, datasets: removeNulls([
								{data: p.visits, label: "Visits", borderColor: p.visitsColor, fill: false},
								p.includesNewVisitors ? {data: p.newVisits, borderColor: p.newVisitsColor, fill: false, label: "New Visitors"} : undefined,
								p.includesReturningVisitors ? {data: p.returningVisits, borderColor: p.returningVisitsColor, fill: false, label: "Returning Visitors"} : undefined,
							])}} />
					</div>
				</div>
			</td>
		</tr>
		</tbody>
	</table>
	<table style={{width:1000, border: 0}} cellSpacing="0" cellPadding="0">
		<tbody><tr style={{height:20}}>
			<td style={{height:20}} />
		</tr>
		<tr>
			<td><h2 className="sbHeading">Site Usage</h2>
				<table className="ui-corner-all ui-widget-content" style={{width:"100%", background:"#eeeeee"}} cellSpacing="0" cellPadding="0" >
					<tbody><tr>
						<td>
							<table cellSpacing="0" cellPadding="0" style={{border:0}}>
								<tbody><tr>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0}}>
											Visits: {formatInt(p.totalVisits)} <HelpButton message="The number of visits to your site." />									</h4>
									</td>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0, color: p.newVisitsColor}}>
											New Visitors: {formatInt(p.totalNewVisits)} <HelpButton message="The number of new visitors to your site."/>									</h4>
									</td>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0, color: p.returningVisitsColor}}>
											Returning Visitors: {formatInt(p.totalReturningVisits)} <HelpButton message="The number of returning visitors to your site." />								</h4>
									</td>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0, color: p.pageviewsColor}}>
											Pageviews: {formatInt(p.totalPageviews)} <HelpButton message="The total number of pages viewed. Repeated views of a single page are counted." />								</h4>
									</td>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0, color: "#000"}}>
											Avg. Time on Site: {secsToHMS(p.averageTimeOnSite)} <HelpButton message="The average duration of a visit to your site." />								</h4>
									</td>
									<td style={{padding: "10px 0 10px 15px"}}>
										<h4 style={{margin: 0, color: "#f00"}}>
											<span style={{color: "#999", fontSize: "11px", fontStyle: "normal"}}>Pages Per Visit:</span> {formatFloat(p.pagesPerVisit, 2)} <HelpButton message="The average number of pages viewed during a visit to your site. Repeated views of a single page are counted." />								</h4>
									</td>
								</tr>
								</tbody></table>
						</td>
					</tr>
					</tbody></table>
				<div id="helpdialog">
					<div className="helpmessage"/>
				</div>
			</td>
		</tr>
		</tbody></table>

	<table>
		<tbody><tr style={{height:20}}>
			<td style={{height:20}}/>
		</tr>
		<tr>
			<td><h2 className="sbHeading">Pageviews</h2>
				<div id="pageviewsDiv2" style={{width:490, height:230}}>

					<Line options={{maintainAspectRatio: false, legend: {display: false}, title: {display: true, text: "Pageviews for all visitors"}}}
						onElementsClick={(e) => e[0] && p.showDayById(e[0]._index)}
						data={{labels: p.labels, datasets: [
								{data: p.pageviews, label: "Pageviews", borderColor: p.pageviewsColor, fill: false},
							]}} />

				</div>

			</td>
			<td style={{width:20}}/>
			<td><h2 className="sbHeading">Traffic Sources Overview</h2>
				<div id="trafficDiv2" style={{width:490, height:230}}>

					{p.sources && <TrafficSourcesPie data={p.sources} directColor={p.directColor} searchColor={p.searchColor} otherColor={p.otherColor} referringColor={p.referringColor} />}

				</div>

			</td>
		</tr>
		<tr style={{height:20}}>
			<td style={{height:20}}/>
		</tr>
		</tbody>
	</table>
	<Tabs active={p.activeTab} onActivate={p.switchTab}>
		<Panel name="Top Traffic Sources" id="traffic sources">
			<DatagridPanel
				makeRow={makeSourceRow}
				headings={trafficSourcesHeadings}
				index='traffic sources'
				title="Top Traffic Sources"
				data={p.tabs}
				callbacks={p.paginationCallbacks}
			/>
		</Panel>
		<Panel name="Top Keywords" id="keywords">
			<DatagridPanel
				makeRow={makeKeywordRow}
				headings={keywordsHeadings}
				index="keywords"
				title="Top Keywords"
				data={p.tabs}
				callbacks={p.paginationCallbacks}
			/>
		</Panel>
		<Panel name="Content Overview" id="content">
			<DatagridPanel
				makeRow={makeContentOverviewRow}
				headings={contentOverviewHeadings}
				index='content'
				title="Content Overview"
				data={p.tabs}
				callbacks={p.paginationCallbacks}
			/>
		</Panel>
		{p.hasEventTracking &&
		<Panel name="Event Tracking Overview" id="event detail">
			<DatagridPanel
				makeRow={makeContentOverviewRow}
				headings={contentOverviewHeadings}
				index='event detail'
				title="Event Tracking Overview"
				data={p.tabs}
				callbacks={p.paginationCallbacks}
			/>
		</Panel>
		}
		{p.hasEcommerceTracking &&
		<Panel name="Ecommerce Overview" id="ecommerce">
			<DatagridPanel
				makeRow={makeEcommerceRow}
				headings={ecommerceOverviewHeadings}
				index='ecommerce'
				title="Ecommerce Overview"
				data={p.tabs}
				callbacks={p.paginationCallbacks}
			/>
		</Panel>
		}
	</Tabs>
</div>

const makeKeywordRow = (d: KeywordsDetail): Row<keyof GridFields> => ({
	id: {node: d.i + ".", alignment: "right"},
	data: {node: d.keyword, alignment: "left", minWidth: "48%"},
	visits: {node: d.visitsTotal + "", alignment: "right", minWidth: "18%"},
	time: {node: secsToHMS(d.avgTime), alignment: "right", minWidth: "28%"}
})

const makeSourceRow = (i: SourceInfo): Row<keyof GridFields> => ({
	id: {node: i.i + ".", alignment: "right"},
	data: {node: <span>{i.source} <span style={{color: "#777"}}>{i.medium}</span></span>, alignment: "left", minWidth: "48%"},
	visits: {node: i.visits + "", alignment: "right", minWidth: "18%"},
	time: {node: secsToHMS(i.avgTimeOnSite), alignment: "right", minWidth: "28%"}
})

const makeEcommerceRow = (i: EcommerceDetail): Row<keyof GridFields> => ({
	id: {node: i.i + ".", alignment: "right"},
	data: {node: <span>{i.productName}</span>, alignment: "left", minWidth: "48%"},
	visits: {node: `${i.uniquePurchasesTotal}`, alignment: "right", minWidth: "18%"},
	time: {node: `$${i.itemRevenueTotal}`, alignment: "right", minWidth: "28%"}
})

const makeContentOverviewRow = (i: ContentDetail): Row<keyof GridFields> => ({
	id: {node: i.i + ".", alignment: "right"},
	time: {node: secsToHMS(i.time), alignment: "right", minWidth: "28%"},
	data: {alignment: "left", minWidth: "48%",
		node:
			<span>
			<a href={i.url} target="_new" title="View this link"><img src="/sb/static/images/url_icon.gif"/></a> {i.maintitle}
				<span style={{color: "#777"}}>({i.greytitle})</span>
		</span>
	},
	visits: {node: i.pageviewsTotal, alignment: "right", minWidth: "18%"}
})

const DatagridPanel = <Index extends keyof AllTabData>(p: {
	title: string,
	data: AllTabData,
	callbacks: (l: Index) => PageCallbackProps,
	headings: GridFields,
	index: Index,
	makeRow: (t: AllTabData[Index]['typePlaceholder']) => Row<keyof GridFields>
}) => {
	const Grid  = GridMaker<AllTabData[Index]['typePlaceholder']>(p.makeRow, p.headings)
	const data = p.data[p.index]

	return <span>
		<h3 style={{margin: "0 0 5px", color: "#036"}}>{p.title}</h3>
		{data.data ? <Grid
			pagination={{...data.pagination, ...p.callbacks(p.index)}}
			data={data.data}
		/> : <span>No data to display</span>}
	</span>
}

const Tabs: (a: TabsProps<Tab>) => JSX.Element = GenericTabs
const Panel: (a: PanelProps<Tab>) => JSX.Element = GenericPanel

type GridFields = {id: string, data: string, visits: string, time: string}

const GridMaker = <T extends {}>(makeRow: (t: T) => Row<keyof GridFields>, headings: GridFields) => (props: {pagination: PageProps, data: T[]}) => {
	return <PaginatedDataGridOfFields
		{...props.pagination}
		heading={headings}
		data={props.data.map(makeRow)}
	/>
}

class PaginatedDataGridOfFields extends PaginatedDataGrid<GridFields>{}

const keywordsHeadings = {id: "", data: "Keyword", visits: "Visits", time: "Avg. Time On Site"};
const trafficSourcesHeadings = {id: "", data: "Source/Medium", visits: "Visits", time: "Avg. Time On Site"};
const contentOverviewHeadings = {id: "", data: "Page", visits: "Pageviews", time: "Avg. Time On Site"};
const ecommerceOverviewHeadings = {id: "", data: "Product", visits: "Quantity", time: "Product Revenue"};

const TrafficSourcesPie = (props: {data: DashboardSourcesEntry<number>, directColor: string, searchColor: string, referringColor: string, otherColor: string}) =>
	<Pie
		data={{
			datasets: [{
				backgroundColor: [props.searchColor, props.referringColor, props.directColor, props.otherColor],
				data: [props.data.organicTotal || 0, props.data.referralTotal || 0, props.data.noneTotal || 0, props.data.otherTotal || 0]
			}],
			labels: ["Search Engines", "Referring Sites", "Direct Traffic", "Other"],
		}}
	/>



// WEBPACK FOOTER //
// ./src/analytics/dashboard/presentation.tsx